import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { FloatingReelService, ReelConfig } from '../../../services/floating-reel.service';

@Component({
  selector: 'app-floating-video',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './floating-video.html',
  styleUrl: './floating-video.scss'
})
export class FloatingVideoComponent implements OnInit, OnDestroy {
  isVisible = signal(false);
  isExpanded = signal(false);
  isVideoLoaded = signal(false);
  currentReel = signal<ReelConfig | null>(null);
  safeEmbedUrl = signal<SafeResourceUrl | null>(null);
  
  private hideTimeout: any;
  private readonly hideDelay = 3000; // Show for 3 seconds after page load

  constructor(
    private router: Router,
    private floatingReelService: FloatingReelService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    // Listen to route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.handleRouteChange(event.url);
    });

    // Subscribe to reel changes from service
    this.floatingReelService.currentReel$.subscribe(reel => {
      this.currentReel.set(reel);
      if (reel?.embedUrl) {
        this.safeEmbedUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(reel.embedUrl));
      }
    });

    // Initial route check
    this.handleRouteChange(this.router.url);
  }

  ngOnDestroy() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  private handleRouteChange(url: string) {
    // Show on home page, products page, AND product detail pages
    const shouldShowVideo = url === '/' || 
                           url === '/home' || 
                           url === '/products' || 
                           url.includes('/product/');
    
    if (shouldShowVideo) {
      // Reset states and force re-initialization
      this.isVisible.set(false);
      this.isExpanded.set(false);
      this.isVideoLoaded.set(false);
      
      // Use service to set appropriate reel for route
      this.floatingReelService.setReelForRoute(url);
      
      // Show the floating video after a delay (re-initialize from bottom)
      this.scheduleShow();
    } else {
      this.isVisible.set(false);
    }
  }

  private scheduleShow() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    
    this.hideTimeout = setTimeout(() => {
      // Double-check route hasn't changed
      const currentUrl = this.router.url;
      const shouldShowVideo = currentUrl === '/' || 
                             currentUrl === '/home' || 
                             currentUrl === '/products' || 
                             currentUrl.includes('/product/');
      
      if (shouldShowVideo) {
        // Re-initialize with slide-in animation from bottom
        this.isVisible.set(true);
      }
    }, this.hideDelay);
  }

  onVideoLoaded() {
    this.isVideoLoaded.set(true);
  }

  onClose() {
    this.isVisible.set(false);
    this.isExpanded.set(false);
  }

  onExpand() {
    this.isExpanded.set(!this.isExpanded());
  }

}
